import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { ModelInstance, Collection } from 'miragejs'
import { times as _times } from 'lodash-es'
import {
  rand,
  randPastDate,
  randNumber,
  randChanceBoolean,
  randEmail,
  randFirstName,
  randParagraph,
  randTextRange,
} from '@ngneat/falso'
import {
  sortable,
  pageable,
  filterable,
  createErrorResponse,
  generateRandomTimeString,
  shuffleArrayElements,
} from '@@/api/mock-server/utils.js'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import {
  ScheduleReportFrequency,
  ScheduleReportDataCollectionPeriod,
  ScheduleReportExportFormat,
  ScheduleReportCustomFrequencyUnit,
} from '@/models/reports/scheduleReport.js'
import { WeekDay } from '@/utils/date/getter.js'
import type { ScheduleReportAttributes } from '@/composables/api/ScheduleReport.js'

export const model = Model as ModelDefinition<ScheduleReportAttributes>

export const factory = Factory.extend({
  report_type() {
    return rand([
      'invoice_v2',
      'consultation_metrics',
      'provet_pay_payments_report_v1',
    ])
  },
  report_filters() {
    return null
  },
  status() {
    return rand(['PENDING', 'SENT', 'FAILED'])
  },
  start_date() {
    return randPastDate().toISOString().split('T')[0]
  },
  start_time() {
    return generateRandomTimeString()
  },
  timezone() {
    return rand([
      'UTC',
      'Europe/Amsterdam',
      'Europe/Helsinki',
      'Europe/London',
      'Europe/Paris',
      'America/New_York',
      'America/Denver',
      'America/Los_Angeles',
    ])
  },
  frequency_config() {
    const freqType = rand(Object.values(ScheduleReportFrequency))
    const isCustom = freqType === ScheduleReportFrequency.Custom
    const isWeekly = freqType === ScheduleReportFrequency.Weekly

    return {
      freq_type: freqType,
      interval: isCustom ? randNumber({ min: 1, max: 10 }) : null,
      unit: isCustom
        ? rand(Object.values(ScheduleReportCustomFrequencyUnit))
        : null,
      weekdays: isWeekly ? [rand(Object.values(WeekDay))] : null,
      monthly_option:
        freqType === ScheduleReportFrequency.Monthly ? 'FIRST_DAY' : null,
      monthly_day:
        freqType === ScheduleReportFrequency.Monthly
          ? randNumber({ min: 1, max: 28 })
          : null,
      cron: null,
      day_of_week: isWeekly ? randNumber({ min: 1, max: 7 }) : null,
    }
  },
  report_formats() {
    const options = Object.values(ScheduleReportExportFormat)
    const removeCount = randNumber({ min: 0, max: options.length - 1 })
    const shuffled = shuffleArrayElements(options)
    return shuffled.slice(0, options.length - removeCount)
  },
  period() {
    return rand(Object.values(ScheduleReportDataCollectionPeriod))
  },
  apply_filters() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  from_email() {
    return randEmail({
      firstName: randFirstName(),
      provider: rand(['example.org', 'test.com', 'demo.net']),
    })
  },
  to_emails() {
    return _times(randNumber({ min: 1, max: 5 }), () =>
      randEmail({
        firstName: randFirstName(),
        provider: 'some-bath-clinic',
      }),
    )
  },
  subject() {
    return randTextRange({
      min: 10,
      max: 30,
    }).replace(/\.$/, '')
  },
  body() {
    return randParagraph()
  },
  is_paused() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  is_expired() {
    return randChanceBoolean({ chanceTrue: 0.1 })
  },
  report_data() {
    return {}
  },
})

export function routes(server: MockServer) {
  server.get(
    '/reports/schedule',
    function (this: MirageServer, schema, request) {
      const allScheduleReports = schema.all('scheduleReport') as Collection<
        ModelInstance<ScheduleReportAttributes>
      >

      // 2) Filter using the established utility
      const filtered = filterable(allScheduleReports, request, {
        search: (item, value) => {
          const searchTerm = value.toLowerCase()
          const reportSubject = item.subject?.toLowerCase() || ''
          const reportMessage = item.body?.toLowerCase() || ''

          return (
            reportSubject.includes(searchTerm) ||
            reportMessage.includes(searchTerm)
          )
        },
        is_paused: (item, value) => item.is_paused === (value === 'true'),
        is_expired: (item, value) => item.is_expired === (value === 'true'),
      })

      // 2) Sort
      const sorted = sortable(filtered, request)

      // 3) Paginate
      const { data, meta } = pageable(sorted, request)

      return {
        data: data.models.map((report) => {
          return {
            id: report.id,
            type: 'ScheduledReport',
            attributes: report.attrs,
          }
        }),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get('/reports/schedule/:id', (schema, request) => {
    const id = request.params.id
    const scheduleReport = schema.find(
      'scheduleReport',
      id,
    ) as ModelInstance<ScheduleReportAttributes> | null

    if (!scheduleReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Schedule report with id ${id} not found` }],
      })
    }

    return {
      data: {
        id: scheduleReport.id,
        type: 'ScheduledReport',
        attributes: scheduleReport.attrs,
      },
    }
  })

  server.post('/reports/schedule', async (schema, request) => {
    const requestBody = JSON.parse(request.requestBody)
    const { attributes } = requestBody.data
    const createdScheduleReport = await schema.create(
      'scheduleReport',
      attributes,
    )

    return {
      data: {
        id: createdScheduleReport.id,
        type: 'ScheduledReport',
        attributes: createdScheduleReport.attrs,
      },
    }
  })

  server.patch('/reports/schedule/:id', async (schema, request) => {
    const id = request.params.id
    const requestBody = JSON.parse(request.requestBody)
    const scheduleReport = schema.find(
      'scheduleReport',
      id,
    ) as ModelInstance<ScheduleReportAttributes> | null

    if (!scheduleReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Schedule report with id ${id} not found` }],
      })
    }

    if (!requestBody?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    const newAttributes = requestBody.data.attributes
    await scheduleReport.update({
      ...scheduleReport.attrs,
      ...newAttributes,
    })

    return {
      data: {
        id: scheduleReport.id,
        type: 'ScheduledReport',
        attributes: scheduleReport.attrs,
      },
    }
  })

  server.delete('/reports/schedule/:id', async (schema, request) => {
    const id = request.params.id
    const scheduleReport = schema.find(
      'scheduleReport',
      id,
    ) as ModelInstance<ScheduleReportAttributes> | null

    if (!scheduleReport) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Schedule report with id ${id} not found` }],
      })
    }

    await scheduleReport.destroy()

    return new Response(204, {}, '')
  })
}

export function seeds(server: MockServer) {
  // Create general scheduling reports
  server.createList('scheduleReport', 15)
}
